/**
 * GLDN Beam Drawer Re-hydrate
 *
 * The Beam <beam-select-nonprofit> web component initializes on page
 * load. When it lives inside the cart drawer dialog (which is hidden
 * with display:none until opened), the SDK's first render runs against
 * the hidden tree and may skip painting the "selected nonprofit" card
 * portion of the widget. Cart attribute persists correctly
 * (nonprofit_id stays set), but the visible UI shows only the icon
 * picker without the selection card.
 *
 * On every dialog:open event for the cart drawer, detach and re-attach
 * each <beam-select-nonprofit> inside the drawer. The SDK's
 * disconnectedCallback + connectedCallback fire, the component re-reads
 * cart attributes, and the selected state renders correctly.
 *
 * Cart page widget (in main-cart section, not in a dialog) is
 * untouched — it renders correctly on first paint already.
 *
 * gldn143 (#496): the drawer picker is no longer in the DOM at page load —
 * header-actions.liquid holds it in <template data-gldn-beam-picker>. This
 * script owns expanding it (ensurePickerInserted) on the first real cart
 * intent, and every rehydrate path is a no-op until that has happened, so it
 * can never rehydrate an absent or un-upgraded element. Insertion always
 * precedes the first rehydrate pass.
 */
(function () {
  /* rehydrateBeamIn works by DETACHING and re-attaching the widget, so for one
   * or more frames the container legitimately has no <beam-select-nonprofit>.
   * keepPickerMounted() must not read that window as "the morph ate the
   * picker" and clone a second one — that produced 21 stacked pickers and a
   * 1758px drawer before this guard existed (#496). */
  var rehydrating = 0;

  function rehydrateBeamIn(scope) {
    if (!scope) return;
    var widgets = scope.querySelectorAll('beam-select-nonprofit');
    /* #496: nothing to rehydrate before the picker is cloned in. Returning
     * here (rather than throwing on a null parentNode) is what makes the
     * lazy-insert change safe for every pre-existing caller. */
    if (!widgets.length) return;
    widgets.forEach(function (w) {
      var parent = w.parentNode;
      if (!parent) return;
      var next = w.nextSibling;
      // Detach + re-attach to force connectedCallback to fire again.
      rehydrating++;
      parent.removeChild(w);
      // Yield a frame so the SDK's disconnect handlers settle, then
      // reinsert at the same position.
      requestAnimationFrame(function () {
        parent.insertBefore(w, next);
        rehydrating--;
      });
    });
  }

  /* Did the widget actually render the selected-nonprofit card? The SDK
   * paints children (text + progress bar) into the <beam-select-nonprofit>
   * subtree once it's read the cart attribute. If the widget is still
   * "empty" (icon-picker only with no children rendered inside the
   * web component), the rehydrate is worth another retry. */
  function widgetLooksHydrated(widget) {
    if (!widget) return false;
    // Widgets that have read cart state and have a selection painted
    // typically have non-empty inner content (the selected-nonprofit
    // card + progress bar). Empty/un-hydrated widgets are bare custom
    // elements with no internal markup yet.
    var inner = (widget.textContent || '').trim();
    return inner.length > 0;
  }

  function rehydrateWithRetries(scope) {
    /* The SDK's initialization may race the dialog:open timing. Try
     * several times so we cover the cases where:
     * - SDK is fully ready: first call finishes the job
     * - SDK still loading: later calls catch up once it's done
     * Stop retrying as soon as the widget looks hydrated. */
    var delays = [100, 400, 900, 1800];
    delays.forEach(function (ms) {
      setTimeout(function () {
        var widgets = scope.querySelectorAll('beam-select-nonprofit');
        var anyUnhydrated = false;
        widgets.forEach(function (w) {
          if (!widgetLooksHydrated(w)) anyUnhydrated = true;
        });
        if (anyUnhydrated) rehydrateBeamIn(scope);
      }, ms);
    });
  }

  /* --- #496: lazy picker insertion --- */

  /* Clone <template data-gldn-beam-picker> into its .gldn-drawer-beam parent,
   * once. Returns true when a picker is present afterwards. Setting
   * data-beam-inserted is what reveals the container and reserves its height
   * (see .gldn-drawer-beam[data-beam-inserted] in the async overrides). */
  function ensurePickerInserted(scope) {
    var root = scope || document;
    var containers = root.querySelectorAll('.gldn-drawer-beam');
    var present = false;
    containers.forEach(function (container) {
      if (container.querySelector('beam-select-nonprofit')) { present = true; return; }
      var tpl = container.querySelector('template[data-gldn-beam-picker]');
      if (!tpl) return;
      /* Drop anything the morph left behind so a re-expand cannot stack a
       * second copy of the widget wrapper. */
      Array.prototype.slice.call(container.children).forEach(function (child) {
        if (child.tagName !== 'TEMPLATE') container.removeChild(child);
      });
      container.appendChild(tpl.content.cloneNode(true));
      container.setAttribute('data-beam-inserted', '');
      present = true;
    });
    return present;
  }

  /* The SDK may not be loaded yet on a deferred page type. gldn-beam-lazy
   * exposes an idempotent arm function; on eager page types it is absent
   * because the SDK is already in the document. */
  function armBeamIfNeeded() {
    if (typeof window.gldnBeamArm === 'function') window.gldnBeamArm('drawer-picker-inserted');
  }

  /* Insert + arm, then hand off to the rehydrate pass. Announcing insertion
   * separately keeps the ordering explicit and testable. */
  function mountDrawerPicker(scope) {
    var inserted = ensurePickerInserted(scope);
    if (!inserted) return false;
    armBeamIfNeeded();
    document.dispatchEvent(new CustomEvent('gldn:beam-picker-inserted'));
    return true;
  }

  /* Pre-warm on the first hover/tap of the cart icon, before the drawer opens.
   * Capture phase so a stopPropagation in the theme's own handler cannot hide
   * the intent from us. */
  function onCartIconIntent(e) {
    var t = e.target;
    if (!t || !t.closest) return;
    if (!t.closest('[data-testid="cart-drawer-trigger"], a[href$="/cart"]')) return;
    mountDrawerPicker(document);
  }
  document.addEventListener('pointerenter', onCartIconIntent, true);
  document.addEventListener('touchstart', onCartIconIntent, true);
  /* A click is its own trigger: a programmatic .click() (mobile auto-open, the
   * personalizer) fires no pointerenter, and a tap that never hovers is real. */
  document.addEventListener('click', onCartIconIntent, true);

  /* PRIMARY insertion trigger: the dialog's `open` attribute.
   *
   * Do NOT rely on 'dialog:open' alone here. Measured on the dev theme (#496):
   * clicking [data-testid="cart-drawer-trigger"] opens the drawer
   * (cart-drawer-component dialog[open] === 1) while a document-level
   * 'dialog:open' listener records NOTHING — the event does not reach document
   * on this path. That was survivable while the picker was server-rendered
   * (a missed rehydrate only cost the selected-nonprofit card), but insertion
   * must not depend on it: the widget would never appear at all. Observing the
   * attribute is the same primitive gldn-beam-lazy.liquid already uses to arm
   * the SDK, and it is proven on this theme. */
  function watchDrawer() {
    var drawer = document.querySelector('cart-drawer-component');
    if (!drawer || !window.MutationObserver) return false;
    new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var el = muts[i].target;
        if (el && el.nodeType === 1 && el.hasAttribute && el.hasAttribute('open')) {
          mountDrawerPicker(drawer);
          rehydrateWithRetries(drawer);
          return;
        }
      }
    }).observe(drawer, { attributes: true, attributeFilter: ['open'], subtree: true });
    return true;
  }
  if (!watchDrawer()) {
    /* The header can render after this deferred script. Retry once at DOM ready. */
    document.addEventListener('DOMContentLoaded', watchDrawer, { once: true });
  }

  /* Secondary, kept for the paths where the theme does dispatch it. Capture
   * phase so a non-bubbling dispatch is still seen. mountDrawerPicker and
   * rehydrateBeamIn are both idempotent, so double-firing is harmless. */
  document.addEventListener('dialog:open', function (e) {
    var dialog = e.target && (e.target.closest && e.target.closest('cart-drawer-component'));
    if (!dialog) return;
    /* Insert before rehydrating: a rehydrate pass over an empty container is a
     * no-op, so without this the first drawer open would never paint. */
    mountDrawerPicker(dialog);
    rehydrateWithRetries(dialog);
  }, true);

  /* If the SDK armed after the picker was already inserted, the custom element
   * upgrades late — run one more pass then. */
  document.addEventListener('gldn:beam-armed', function () {
    var drawer = document.querySelector('cart-drawer-component');
    if (drawer && drawer.querySelector('.gldn-drawer-beam[data-beam-inserted]')) rehydrateWithRetries(drawer);
  });

  /* Also rehydrate after cart updates inside the drawer — Horizon's
   * morphSection may rewrite the widget's surroundings and leave it
   * in a stale render state. */
  document.addEventListener('cart:update', function () {
    var drawer = document.querySelector('cart-drawer-component');
    if (!drawer) return;
    // Only act if the drawer is open; closed drawers will hydrate on
    // their own dialog:open path.
    var dialog = drawer.querySelector('dialog[open]');
    if (!dialog) return;
    /* Do NOT re-expand synchronously here. Measured (#496): the cart:update
     * morph is ASYNC — it awaits the section fetch — so a picker re-inserted
     * on this tick is wiped microseconds later when the morph restores the
     * server-rendered <template>, leaving the open drawer with no widget at
     * all (pickers=0, height=0 after a quantity change). keepPickerMounted()
     * below owns re-expansion; it reacts to the morph instead of racing it. */
    rehydrateWithRetries(drawer);
  });
  /* Keeps the picker mounted for as long as the drawer is open.
   *
   * Any morph of the header section (cart:update, discount update, the #416
   * recover path) re-renders header-actions.liquid and therefore restores the
   * inert <template>, removing the picker we cloned in. Re-inserting on the
   * event is a race the morph wins. Watching the subtree instead means we
   * re-expand whenever the picker actually goes missing, whatever removed it.
   *
   * No loop: inserting sets data-beam-inserted and adds a
   * <beam-select-nonprofit>, so the mutation this triggers fails the
   * "template present AND no picker" test and stops there. */
  function keepPickerMounted() {
    var drawer = document.querySelector('cart-drawer-component');
    if (!drawer || !window.MutationObserver) return false;
    var pending = false;
    new MutationObserver(function () {
      if (pending || rehydrating > 0) return;
      if (!drawer.querySelector('dialog[open]')) return;
      var container = drawer.querySelector('.gldn-drawer-beam');
      if (!container) return;
      if (container.querySelector('beam-select-nonprofit')) return;
      if (!container.querySelector('template[data-gldn-beam-picker]')) return;
      pending = true;
      /* Let the in-flight morph finish before re-expanding, otherwise we are
       * back in the race this function exists to avoid. */
      setTimeout(function () {
        pending = false;
        if (rehydrating > 0) return;
        if (!drawer.querySelector('dialog[open]')) return;
        var c = drawer.querySelector('.gldn-drawer-beam');
        /* Re-test after the delay: the morph may have finished and the
         * rehydrate pass may already have put the widget back. */
        if (!c || c.querySelector('beam-select-nonprofit')) return;
        if (mountDrawerPicker(drawer)) rehydrateWithRetries(drawer);
      }, 150);
    }).observe(drawer, { childList: true, subtree: true });
    return true;
  }
  if (!keepPickerMounted()) {
    document.addEventListener('DOMContentLoaded', keepPickerMounted, { once: true });
  }
})();
